// React
import * as React from "react";
import _ from 'lodash';
import moment from 'moment';

import { Table, TableBody, TableRow, TableRowColumn } from 'material-ui/Table';
import TextField from 'material-ui/TextField';
import IconButton from 'material-ui/IconButton';
import SetIcon from 'material-ui/svg-icons/action/done';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { DateFormat, Formatters } from '@Utils/Formats';
import { getRowMinHeight } from '@Primitive/Typography';
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { DateRangePicker } from '@Primitive/ReactBootstrapDateRangePicker';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import type { LocalizationMap } from '@Primitive/Localization/Localization';

// =======================================================================================================
export interface DatePickerData {
    DatePicker: {
        Key: string;
        Value: string;
        Date: string;
        Locale: string;
        DisplayDatePicker: boolean;
        format: DateFormat;
        startDate: any
        endDate: any;
    }[];
    FillWidget: boolean
}

export interface DatePickerState {
    Data: DatePickerData;
}

// ------------------------------------------------------------------------------------------------------
class DatePicker extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<DatePickerData>, DatePickerState> {
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<DatePickerData>) {
        super(props);

        const data = _.cloneDeep(this.props.WidgetData);
        data.DatePicker.forEach(element => {
            element.Date = (_.has(this.props.PortsStates, element.Key)) ? this.props.PortsStates[element.Key].State : "";
            element.startDate = moment(element.startDate);
            element.endDate = moment(element.endDate);
        });

        this.state = { Data: data };
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<DatePickerData>) {
        if (!_.isEqual(this.props.WidgetData, nextProps.WidgetData))
            this.setState((prevState: DatePickerState) => {
                prevState.Data = _.cloneDeep(nextProps.WidgetData);
                prevState.Data.DatePicker.forEach(element => {
                    element.Date = (_.has(nextProps.PortsStates, element.Key)) ? nextProps.PortsStates[element.Key].State : "";
                    element.startDate = moment(element.startDate);
                    element.endDate = moment(element.endDate);
                });
                return prevState;
            });
    }
    // ---------------------------------------------------------------------------------------------------
    handleClick = (index) => {
        this.setState((prevState) => {
            prevState.Data.DatePicker[index].DisplayDatePicker = !prevState.Data.DatePicker[index].DisplayDatePicker;
            return prevState;
        });
    };
    // ---------------------------------------------------------------------------------------------------
    render() {

        let minHeight = undefined;
        if (this.props.WidgetData.FillWidget)
            minHeight = getRowMinHeight(
                this.props.WidgetData.DatePicker.length,
                this.props.height,
                48,
                0
            );

        return (
            <div style={{ padding: WidgetsThingsStructs.defaultWidgetContainerPadding }}>
                <Table height="100%" selectable={false} multiSelectable={false}
                    style={{ backgroundColor: "transparent" }}>
                    <TableBody
                        displayRowCheckbox={false}
                    /*stripedRows={true}*/
                    >
                        {
                            this.state.Data.DatePicker.map((d, index) => {
                                let portIsActive = (_.has(this.props.PortsStates, d.Key)) ? this.props.PortsStates[d.Key].IsActive : false;

                                let value = this.props.LocalizationMap['value_' + d.Key];
                                if (IsNullOrEmpty(value))
                                    value = d.Value;

                                return (
                                    <TableRow key={"Date-Picker-Row-" + d.Key} >
                                        <TableRowColumn colSpan={2} key={"Row-Column-Label" + d.Key} style={{ textAlign: "center", cursor: (portIsActive) ? "auto" : "not-allowed", height: minHeight, fontSize: this.props.width < 270 ? 10 : 13, paddingLeft: this.props.width < 270 ? 15 : 24 }}>
                                            {value}
                                        </TableRowColumn>
                                        <TableRowColumn colSpan={3} key={"Row-Column-Picker" + d.Key} style={{ paddingLeft: this.props.width < 270 ? 0 : 24 }}>
                                            <DateRangePicker
                                                showDropdowns
                                                startDate={d.startDate}
                                                endDate={d.endDate}
                                                onApply={(event, picker) => {
                                                    this.setState((prevState) => {
                                                        prevState.Data.DatePicker[index].startDate = moment(picker.startDate);
                                                        prevState.Data.DatePicker[index].endDate = moment(picker.endDate);
                                                        prevState.Data.DatePicker[index].Date = Formatters.datetimeRange(picker.startDate, picker.endDate, prevState.Data.DatePicker[index].format, false);
                                                        return prevState;
                                                    });
                                                }}
                                                localizationKey={this.props.NodeKey}
                                            >
                                                <TextField
                                                    key={"Row-Column-Picker-Value" + d.Key}
                                                    id={"Row-Column-Picker-Value" + d.Key}
                                                    value={d.Date}
                                                />
                                            </DateRangePicker>
                                        </TableRowColumn>
                                        <TableRowColumn colSpan={1} key={"Row-Column-Set-Icon-" + d.Key} style={{ textAlign: "center", height: minHeight, paddingLeft: this.props.width < 270 ? 4 : 24 }}>
                                            <IconButton key={"Date-Picker-Set-Icon" + d.Key} style={{ textAlign: "center", opacity: (portIsActive) ? 1 : 0.2, cursor: (portIsActive) ? "pointer" : "not-allowed" }} touch={true}
                                                onClick={() => { if (portIsActive) this.props.SendPortEvent(d.Key, d.Date) }}>
                                                <SetIcon />
                                            </IconButton>
                                        </TableRowColumn>
                                    </TableRow>
                                );
                            })
                        }
                    </TableBody>
                </Table>
            </div>
        );
    }
}

interface DatePickerWidgetSettingsState {
    WidgetData: DatePickerData;
    UnSavedData: boolean;
}

class DatePickerWidgetSettings extends React.Component<IWidgetSettingsProps<DatePickerData>, DatePickerWidgetSettingsState> {
    constructor(props: IWidgetSettingsProps<DatePickerData>) {
        super(props);

        this.state = {
            WidgetData: _.cloneDeep(this.props.WidgetData),
            UnSavedData: false
        };

        // Bind functions
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.WidgetData);
        this.setState({ UnSavedData: false });
    }

    // ------------------------------------------------------------------------------------------------
    render() {

        let settings: SettingsGroup<DatePickerData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'DatePicker',
                        tooltip: 'Define the number of date pickers, their name and their format (Short, Long, ISO).',
                        dataPath: "DatePicker",
                        dialogStaticSize: { w: 0.7, h: 0.8 },
                        itemsInfo: [
                            {
                                type: SettingsType.String,
                                label: "DatePicker",
                                dataPath: "Value"
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "DatePicker Format",
                                dataPath: "format",
                                items: [
                                    { value: DateFormat.Short, label: "Short" },
                                    { value: DateFormat.Long, label: "Long" },
                                    { value: DateFormat.ISO, label: "ISO" }
                                ]
                            }
                        ],
                        createNewItem: (data) => {
                            let thing = WebNode.GetInstanceKey(this.props.NodeKey).GetThing(this.props.ThingKey);
                            return {
                                Key: thing.GetNextGeneratedPortKey(),
                                Value: "Date Picker",
                                Date: "",
                                Locale: "en",
                                DisplayDatePicker: false,
                                startDate: moment(),
                                endDate: moment(),
                                format: DateFormat.Long
                            }
                        }
                    },
                    {
                        type: SettingsType.Boolean,
                        tooltip: "Fills the widget's available space.",
                        label: "Fill Space",
                        dataPath: "FillWidget"
                    }
                ]
            },
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
// Date Picker Description interface
export class DatePickerWidget extends IWidgetThingsClass<DatePickerData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <DatePicker />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <DatePickerWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        _.forEach(wData.DatePicker, (d, index) => {
            thing.AddPort(new WebThingPort(
                {
                    Name: d.Value,
                    PortKey: d.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "",
                    State: d.Date,
                }, WebThingPortType.Data), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        _.forEach(wData.DatePicker, (btn, index) => {
            ports.push(new WebThingPort(
                {
                    Name: btn.Value,
                    PortKey: btn.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "",
                    State: ""
                }, WebThingPortType.Data
            ));
        });

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): DatePickerData {
        return {
            DatePicker: [],
            FillWidget: false
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return DatePickerWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: DatePickerData = {
            DatePicker: [
                {
                    Key: "1_" + getRandomSmallKey(), Value: "Date picker 1", Date: "", Locale: "en", DisplayDatePicker: false, startDate: moment(), endDate: moment(), format: DateFormat.Short
                }],
            FillWidget: false
        };

        return {
            Title: "Date Picker",
            WidgetTitle: "",
            Key: "DatePickerWidget",
            Type: "DatePickerWidget",
            Data: data,
            Description: "Date Picker widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false, IconURI: WidgetsThingsStructs.IconURIPath + "/svg/datepicker.svg"

        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = {};

        // expose title (if any)
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.DatePicker, (picker) => {
            if (!IsNullOrEmpty(picker.Value))
                res['value_' + picker.Key] = picker.Value;
        });

        return res;
    };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================